import React from "react";

export type AcademicAssignmentOption = {
  id: number;
  name: string;
  stage_id?: number | null;
  stage_ids?: number[];
};

type AssignmentGroupProps = {
  title: string;
  emptyText: string;
  options: AcademicAssignmentOption[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  disabled?: boolean;
};

function AssignmentGroup({
  title,
  emptyText,
  options,
  selectedIds,
  onToggle,
  disabled = false,
}: AssignmentGroupProps) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-white">{title}</p>
        {selectedIds.length > 0 && (
          <span className="text-xs text-teal-300">{selectedIds.length}</span>
        )}
      </div>
      {options.length > 0 ? (
        <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto pe-1">
          {options.map((option) => {
            const selected = selectedIds.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(option.id)}
                className={`rounded-xl border px-3 py-2 text-sm transition ${
                  selected
                    ? "border-teal-300 bg-teal-500 text-white shadow-lg shadow-teal-950/20"
                    : "border-white/10 bg-white/[0.04] text-white/65 hover:border-teal-300/50 hover:text-white"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {option.name}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-white/10 px-3 py-3 text-xs text-white/40">
          {emptyText}
        </p>
      )}
    </div>
  );
}

type InstructorAcademicAssignmentsProps = {
  isArabic: boolean;
  stages: AcademicAssignmentOption[];
  grades: AcademicAssignmentOption[];
  subjects: AcademicAssignmentOption[];
  stageIds: number[];
  gradeIds: number[];
  subjectIds: number[];
  loading: boolean;
  onStageToggle: (id: number) => void;
  onGradeToggle: (id: number) => void;
  onSubjectToggle: (id: number) => void;
};

export default function InstructorAcademicAssignments({
  isArabic,
  stages,
  grades,
  subjects,
  stageIds,
  gradeIds,
  subjectIds,
  loading,
  onStageToggle,
  onGradeToggle,
  onSubjectToggle,
}: InstructorAcademicAssignmentsProps) {
  const selectedStageIds = new Set(stageIds);
  const selectedStages = stages.filter((stage) => selectedStageIds.has(stage.id));
  const gradeGroups = selectedStages.map((stage) => ({
    ...stage,
    options: grades.filter((grade) => Number(grade.stage_id) === stage.id),
  }));
  const subjectGroups = selectedStages.map((stage) => ({
    ...stage,
    options: subjects.filter((subject) => (subject.stage_ids?.length ? subject.stage_ids : [subject.stage_id]).some((id) => Number(id) === stage.id)),
  }));
  const groupedSectionClass = "space-y-4";
  const groupDividerClass = "border-t border-white/10 pt-4 first:border-t-0 first:pt-0";

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5">
      <div className="mb-4">
        <h2 className="text-base font-bold text-white">
          {isArabic ? "التخصصات الأكاديمية" : "Academic assignments"}
        </h2>
        <p className="mt-1 text-xs text-white/45">
          {isArabic
            ? "اختر المراحل والصفوف والمواد التي تدرّسها."
            : "Choose the stages, grades, and subjects you teach."}
        </p>
      </div>

      {loading ? (
        <p className="rounded-xl bg-white/[0.04] px-3 py-4 text-sm text-white/55">
          {isArabic ? "جاري تحميل البيانات الأكاديمية..." : "Loading academic options..."}
        </p>
      ) : (
        <div className="space-y-5">
          <AssignmentGroup
            title={isArabic ? "المراحل *" : "Stages *"}
            emptyText={isArabic ? "لا توجد مراحل متاحة حالياً." : "No stages available."}
            options={stages}
            selectedIds={stageIds}
            onToggle={onStageToggle}
          />
          <div className={groupedSectionClass}>
            {gradeGroups.length > 0 ? gradeGroups.map((group) => (
              <div key={group.id} className={groupDividerClass}>
                <AssignmentGroup
                  title={`${isArabic ? "صفوف" : "Grades"} ${group.name}`}
                  emptyText={isArabic ? "لا توجد صفوف لهذه المرحلة." : "No grades for this stage."}
                  options={group.options}
                  selectedIds={gradeIds}
                  onToggle={onGradeToggle}
                  disabled={stageIds.length === 0}
                />
              </div>
            )) : (
              <AssignmentGroup
                title={isArabic ? "الصفوف *" : "Grades *"}
                emptyText={stageIds.length > 0 ? (isArabic ? "لا توجد صفوف لهذه المراحل." : "No grades are available for the selected stages.") : (isArabic ? "اختر مرحلة أولاً." : "Select a stage first.")}
                options={[]}
                selectedIds={gradeIds}
                onToggle={onGradeToggle}
                disabled={stageIds.length === 0}
              />
            )}
          </div>
          <div className={groupedSectionClass}>
            {subjectGroups.length > 0 ? subjectGroups.map((group) => (
              <div key={group.id} className={groupDividerClass}>
                <AssignmentGroup
                  title={`${isArabic ? "مواد" : "Subjects"} ${group.name}`}
                  emptyText={isArabic ? "لا توجد مواد لهذه المرحلة." : "No subjects for this stage."}
                  options={group.options}
                  selectedIds={subjectIds}
                  onToggle={onSubjectToggle}
                  disabled={stageIds.length === 0}
                />
              </div>
            )) : (
              <AssignmentGroup
                title={isArabic ? "المواد *" : "Subjects *"}
                emptyText={stageIds.length > 0 ? (isArabic ? "لا توجد مواد لهذه المراحل." : "No subjects are available for the selected stages.") : (isArabic ? "اختر مرحلة أولاً." : "Select a stage first.")}
                options={[]}
                selectedIds={subjectIds}
                onToggle={onSubjectToggle}
                disabled={stageIds.length === 0}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
